<template>
  <v-flex column>
    <v-divider v-show="!firstOrderLine" />
    <v-layout ml-7 mr-3 py-4>
      <v-layout align-self-start :class="{ 'max-width-190': !isPaid && mdAndUp }">
        <v-container row wrap class="ma-0">
          <v-layout row ma-0 align-center>
            <!-- ORDER LINE NAME -->
            <span class="order-line-name">{{ orderLine.name }}</span>
            <!-- PRICE AND DISPLAY CURRENCY PRICE -->
            <v-layout column align-end justify-start>
              <div v-for="(price, i) in priceArray" :key="i"
                :set1="discountedAmount = getPriceDiscountedAmount(price)"
                :set2="currency = getCurrency(price)"
                :set3="amount = (price.amount + discountedAmount + modifierAmount) * orderLine.quantity"
                class="text-right"
                :class="{
                  'red--text': !orderLine.price.length
                }">
                <v-layout v-if="amount && showDisplayCurrency" column :class="{ 'line-through': discountOrderLines.length }" class="bold-text">
                  <span 
                    class="display-currency__amount-text" 
                    :class="{ 'line-through-grey': discountOrderLines.length }"
                  >
                    {{ getPriceString({ amount, currency: currency, displayCurrency: displayCurrency }).split(' ')[0] }}
                    <span class="display-currency__currency-text">
                      {{ getPriceString({ amount, currency: currency, displayCurrency: displayCurrency }).split(' ')[1] }}
                    </span>
                  </span>
                </v-layout>
                <v-layout v-if="amount" :class="{ 'line-through': discountOrderLines.length }" class="bold-text">
                  <span class="currency__amount-text">
                    {{ getPriceString({ amount, currency: currency }).split(' ')[0] }}
                    <span class="currency__currency-text">
                      {{ getPriceString({ amount, currency: currency }).split(' ')[1] }}
                    </span>
                  </span>
                </v-layout>
                <v-layout v-if="discountedAmount" class="bold-text">
                  <span class="color-text">(-{{ getDiscountValue(price) }}%) {{ getPriceString({ amount, currency: currency }) }}</span>
                  <span v-if="showDisplayCurrency" class="color-text">
                    {{ getPriceString({ amount, currency: currency, displayCurrency: displayCurrency }) }}
                  </span>
                </v-layout>
              </div>
            </v-layout>
            <!-- QUANTITY -->
            <v-avatar class="quantity-badge ml-3">{{ quantity }}</v-avatar>
          </v-layout>
          <!-- ORDER MODIFIERS -->
          <v-flex xs12>
            <v-layout column justify-center>
              <!-- <span class="order-line-name">{{ orderLine.name }}</span> -->
              <v-flex v-show="modifierOptionOrderLines.length" align-self-start>
                <v-layout row align-center class="mx-0 mb-0 mt-1">
                  <template v-for="(orderLine, index) in modifierOptionOrderLines">
                    <span :key="index" class="modifier-text">
                      {{ orderLine.name }} 
                    </span>
                    <span :key="`${index}-point`" class="mx-1">
                      {{ index +1 !== modifierOptionOrderLines.length ? '·' : ''}} 
                    </span> 
                  </template>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-container>
      </v-layout>
    </v-layout>
  </v-flex>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { orderLineTypes, orderLineItemEntities, currencyTypes } from '@/config'

import moneyMixins from '@/mixins/MoneyFormat'
import vuetifyMixins from '@/mixins/Vuetify'

const { CATALOG_PRODUCT_MODIFIER_OPTION } = orderLineItemEntities

export default {
  props: {
    isPaid: Boolean,
    orderLine: Object,
    orderLines: Array,
    currencies: Array, // Finds currency if not decorated
    firstOrderLine: Boolean,
  },
  mixins: [
    moneyMixins,
    vuetifyMixins,
  ],
  data () {
    return {
      PRODUCT: orderLineItemEntities.PRODUCT,
      LEGAL: currencyTypes.LEGAL,
    }
  },
  computed: {
    ...mapState({
      account: (state) => state.accounts.account,
    }),
    ...mapGetters('me', ['getPriceString', 'showDisplayCurrency', 'displayCurrency']),
    quantity () {
      return this.orderLine.quantity
    },
    discountOrderLines () {
      return this.orderLines ? this.orderLines.filter(oL => {
        return (
          oL.id !== this.orderLine.id &&
          oL.parentOrderLine === this.orderLine.id &&
          oL.orderLineType === orderLineTypes.DISCOUNT
        )
      }) : []
    },
    priceArray () {
      return this.orderLine.price.length ? this.orderLine.price : []
    },
    modifierOptionOrderLines () {
      return this.orderLines.filter(({ parentOrderLine, itemEntity }) => parentOrderLine === this.orderLine.id && itemEntity === CATALOG_PRODUCT_MODIFIER_OPTION)
    },
    modifierAmount () {
      return this.modifierOptionOrderLines.filter(({ includedOnParentLine }) => !includedOnParentLine).reduce((acc, orderLine) => {
        return acc + orderLine.price[0].amount
      }, 0)
    },
  },
  methods: {
    getCurrency (price) {
      return price.currency || (
        this.currencies
          ? this.currencies.find(c => c.id === price.currencyId)
          : this.account.currencies.find((c) => c.id === price.currencyId)
      )
    },
    getPriceDiscountedAmount (price) {
      let discountedAmount = 0
      this.discountOrderLines.forEach((discountOrderLine) => {
        const discountForPriceItem = discountOrderLine.price.find((discountPriceItem) => {
          return discountPriceItem.currencyId === price.currencyId
        })
        if (discountForPriceItem) {
          discountedAmount += Math.abs(discountForPriceItem.amount) * discountOrderLine.quantity
        }
      })
      return discountedAmount
    },
    getDiscountValue (price) {
      let value = 0
      this.discountOrderLines.forEach((discountOrderLine) => {
        const discountForPriceItem = discountOrderLine.price.find((discountPriceItem) => {
          return discountPriceItem.currencyId === price.currencyId
        })
        if (discountForPriceItem) {
          value += Math.abs(discountOrderLine.value)
        }
      })
      return value
    },
  },
  components: {
    AmountBadge: () => import('@/components/view-components/catalog/AmountBadge.vue'),
  },
}
</script>

<style lang="scss" scoped>

.quantity-badge {
  height: 25px !important;
  width: 25px !important;
  min-width: 25px !important;
  color: var(--v-primary-lighten1);
  background-color: rgba(var(--v-primary-lighten1-rgb), 0.1);
  text-align: center;
  font-family: 'Mukta Mahee';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.4px;
}

.currency__amount-text {
  color: var(--v-dark-text-base);
  text-align: right;
  font-family: 'Mukta';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 87.5% */
  letter-spacing: 0.22px;
}
.currency__currency-text {
  color: var(--v-dark-text-base);
  font-family: 'Mukta';
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.22px;
}
.display-currency__amount-text {
  color: var(--v-dark-text-base);
  text-align: right;
  font-family: 'Mukta';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px; /* 66.667% */
  letter-spacing: 0.22px;
}
.display-currency__currency-text {
  color: var(--v-dark-text-base);
  font-family: 'Mukta';
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;
  letter-spacing: 0.22px;
}
.order-line-name {
  color: var(--v-dark-text-base);

  /* H4 */
  font-family: 'Urbanist';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.25px;
}
.modifier-text {
  color: var(--v-dark-text-base);
  font-feature-settings: 'clig' off, 'liga' off;

  /* Body Cards B */
  font-family: 'Mukta Mahee';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 15px; /* 125% */
  letter-spacing: 0.25px;
}
.line-through-grey {
  text-decoration: line-through;
  text-decoration-color: grey;
}
.max-width-190 {
  max-width: 190px;
}
</style>
